import { BookScopePicker, EMPTY_BOOK_SCOPE } from "@/components/lessonGenerator/BookScopePicker";
import { ReferenceSlideThumbnail } from "@/components/lessonGenerator/ReferenceSlideThumbnail";
import { SectionEditorModal } from "@/components/lessonGenerator/SectionEditorModal";
import { TagPill } from "@/components/lessonGenerator/TagPill";
import { describeSection } from "@/components/lessonGenerator/countRules";
import type { GeneratorSection } from "@/components/lessonGenerator/types";
import { readsNothing } from "@/components/lessonGenerator/types";
import type { SortableHandleProps } from "@/lib/dnd/components/Sortable";
import { Sortable } from "@/lib/dnd/components/Sortable";
import {
  type GenerationPreset,
  toPresetInput,
  useGenerationPresets,
} from "@/lib/hooks/lessonGenerator/useGenerationPresets";
import {
  toSavedFields,
  toSectionPresetInput,
  useSectionPresets,
} from "@/lib/hooks/sectionPresets/useSectionPresets";
import { useTags } from "@/lib/hooks/tags/useTags";
import { getTemplateLabel } from "@/lib/slides/slideTemplates";
import { Button, LoadingSpinner } from "@ai-teach/ui-web";
import { verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { HiOutlineLink, HiOutlinePencil, HiOutlineTrash, HiPlus } from "react-icons/hi";
import { RxDragHandleDots2 } from "react-icons/rx";

const THUMBNAIL_WIDTH = 48;

interface Draft {
  presetId: string;
  name: string;
  seriesSlug: string | null;
  levelId: string | null;
  componentId: string | null;
  sections: GeneratorSection[];
}

function toDraft(preset: GenerationPreset): Draft {
  return {
    presetId: preset.id,
    name: preset.name,
    seriesSlug: preset.target.seriesSlug,
    levelId: preset.target.levelId,
    componentId: preset.target.componentId,
    sections: preset.sections,
  };
}

function SectionRow({
  section,
  index,
  followedName,
  dragHandle,
  onEdit,
  onUnlink,
  onRemove,
}: {
  section: GeneratorSection;
  index: number;
  /** The section template this one follows, whose settings win when the preset is applied. */
  followedName: string | null;
  dragHandle?: SortableHandleProps | undefined;
  onEdit: () => void;
  onUnlink: () => void;
  onRemove: () => void;
}) {
  const { tags } = useTags();
  const parentTag = section.parent
    ? (tags.find((one) => one.id === section.parent?.tagId)?.name ?? section.parent.tagId)
    : "";

  return (
    <div className="flex items-start gap-3 rounded-xl border border-gray-200 bg-background px-3 py-3 transition-colors hover:border-gray-300 dark:border-gray-800 dark:hover:border-gray-700">
      {dragHandle && (
        <button
          type="button"
          ref={dragHandle.ref}
          {...dragHandle.attributes}
          {...dragHandle.listeners}
          aria-label="Drag to reorder section"
          className="mt-1 shrink-0 cursor-grab text-gray-300 transition-colors hover:text-gray-500 active:cursor-grabbing dark:text-gray-600 dark:hover:text-gray-400"
        >
          <RxDragHandleDots2 className="h-4 w-4" />
        </button>
      )}
      <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-gray-100 text-[11px] font-semibold text-gray-500 dark:bg-white/5 dark:text-gray-400">
        {index + 1}
      </span>

      <button
        type="button"
        onClick={onEdit}
        className="flex min-w-0 flex-1 items-start gap-3 text-left"
      >
        <ReferenceSlideThumbnail slide={section.referenceSlide.slide} width={THUMBNAIL_WIDTH} />

        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
              {section.name ?? getTemplateLabel(section.template)}
            </span>
            <span className="shrink-0 text-[11px] text-gray-500 dark:text-gray-400">
              {getTemplateLabel(section.template)}
            </span>
            {section.referenceSlide.tagIds.map((id) => {
              const tag = tags.find((one) => one.id === id);
              return tag ? <TagPill key={id} tag={tag} /> : null;
            })}
          </span>

          <span className="mt-1 block truncate text-[11px] text-gray-500 dark:text-gray-400">
            {describeSection(section, parentTag)} · ref{" "}
            <span className="font-mono">{section.referenceSlide.name}</span>
          </span>

          {followedName && (
            <span className="mt-1.5 inline-flex items-center gap-1 rounded-md bg-primary-500/10 px-1.5 py-0.5 text-[11px] font-medium text-primary-600 dark:text-primary-300">
              <HiOutlineLink className="h-3 w-3" />
              follows “{followedName}”
            </span>
          )}

          {section.prompt !== "" && (
            <span className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-gray-600 dark:text-gray-300">
              {section.prompt}
            </span>
          )}

          {readsNothing(section) && (
            <span className="mt-1.5 block text-[11px] text-warning-500">
              Nothing checked — written from the example slide alone.
            </span>
          )}
        </span>
      </button>

      <div className="flex shrink-0 gap-0.5">
        {followedName && (
          <button
            type="button"
            onClick={onUnlink}
            aria-label={`Stop following ${followedName}`}
            title="Keep this preset's own copy of the settings instead"
            className="rounded-lg p-1.5 text-primary-500 transition-colors hover:bg-black/5 hover:text-primary-600 dark:hover:bg-white/5"
          >
            <HiOutlineLink className="h-4 w-4" />
          </button>
        )}
        <button
          type="button"
          onClick={onEdit}
          aria-label="Edit section"
          className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-black/5 hover:text-gray-700 dark:hover:bg-white/5 dark:hover:text-gray-200"
        >
          <HiOutlinePencil className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove section"
          className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-error-500/10 hover:text-error-500"
        >
          <HiOutlineTrash className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

/**
 * The whole set of generation presets, one open at a time: its book scope, and the ordered sections
 * a run starts from. The dropdown inside the generator can only apply and delete them.
 */
export function GenerationPresetPanel() {
  const { presets, loading, savePreset, updatePreset, deletePreset } = useGenerationPresets();
  const { sections: savedSections, updateSection } = useSectionPresets();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [dirty, setDirty] = useState(false);
  // Which followed sections were edited here, so saving writes back only the templates touched.
  const [editedLinks, setEditedLinks] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState<string | null>(null);
  const [editing, setEditing] = useState<GeneratorSection | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);

  const selected = presets.find((one) => one.id === selectedId) ?? null;

  // Loads the open preset once, so a refetch behind a save never overwrites what is being typed.
  useEffect(() => {
    if (!selected || draft?.presetId === selected.id) return;
    setDraft(toDraft(selected));
    setDirty(false);
    setEditedLinks([]);
  }, [selected, draft?.presetId]);

  const edit = (changes: Partial<Draft>) => {
    setDraft((prev) => (prev ? { ...prev, ...changes } : prev));
    setDirty(true);
  };

  const open = (preset: GenerationPreset) => {
    if (dirty && !window.confirm("Leave this preset? Your unsaved changes are lost.")) return;
    setSelectedId(preset.id);
    setDraft(toDraft(preset));
    setDirty(false);
    setEditedLinks([]);
  };

  const create = async () => {
    if (newName === null || newName.trim() === "") return;
    const name = newName.trim();
    setSaving(true);
    try {
      const id = await savePreset(toPresetInput(name, { ...EMPTY_BOOK_SCOPE, lesson: null }, []));
      setNewName(null);
      if (id) setSelectedId(id);
      toast.success(`Preset “${name}” created`);
    } catch {
      toast.error("Could not create the preset");
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    if (!draft) return;
    // A followed section belongs to its template, so editing it here edits the template itself,
    // and reaches every preset that follows it.
    const followed = draft.sections.flatMap((one) =>
      one.sectionPresetId && editedLinks.includes(one.id)
        ? [
            {
              id: one.sectionPresetId,
              name: one.name?.trim() || getTemplateLabel(one.template),
              fields: toSavedFields(one),
            },
          ]
        : []
    );
    setSaving(true);
    try {
      for (const one of followed) {
        await updateSection(one.id, toSectionPresetInput(one.name, one.fields));
      }
      await updatePreset(
        draft.presetId,
        toPresetInput(
          draft.name.trim() || "Untitled preset",
          {
            seriesSlug: draft.seriesSlug,
            levelId: draft.levelId,
            componentId: draft.componentId,
            lesson: null,
          },
          draft.sections
        )
      );
      setDirty(false);
      setEditedLinks([]);
      toast.success(
        followed.length === 0
          ? "Preset saved"
          : `Preset saved · ${followed.map((one) => `“${one.name}”`).join(", ")} updated`
      );
    } catch {
      toast.error("Could not save the preset");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (preset: GenerationPreset) => {
    if (!window.confirm(`Delete “${preset.name}”? This cannot be undone.`)) return;
    try {
      await deletePreset(preset.id);
      if (preset.id === selectedId) {
        setSelectedId(null);
        setDraft(null);
        setDirty(false);
      }
      toast.success(`Preset “${preset.name}” deleted`);
    } catch {
      toast.error("Could not delete the preset");
    }
  };

  const upsertSection = (section: GeneratorSection) => {
    if (!draft) return;
    const index = draft.sections.findIndex((one) => one.id === section.id);
    if (section.sectionPresetId) {
      setEditedLinks((prev) => (prev.includes(section.id) ? prev : [...prev, section.id]));
    }
    edit({
      sections:
        index === -1
          ? [...draft.sections, section]
          : draft.sections.map((one) => (one.id === section.id ? section : one)),
    });
  };

  // Dropping the link keeps the settings as they resolve right now, as this preset's own copy.
  const unlinkSection = (section: GeneratorSection) => {
    if (!draft) return;
    const { sectionPresetId: _unused, ...own } = section;
    edit({ sections: draft.sections.map((one) => (one.id === section.id ? own : one)) });
  };

  const openEditor = (section: GeneratorSection | null) => {
    setEditing(section);
    setIsEditorOpen(true);
  };

  return (
    <div className="flex min-h-0 flex-1 gap-6">
      <aside className="flex w-72 shrink-0 flex-col gap-2 overflow-y-auto">
        {loading && presets.length === 0 ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : presets.length === 0 ? (
          <p className="rounded-xl border border-dashed border-gray-300 px-3 py-6 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
            No presets yet.
          </p>
        ) : (
          <ul className="space-y-1 p-1">
            {presets.map((preset) => (
              <li key={preset.id} className="group flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => open(preset)}
                  className={`min-w-0 flex-1 rounded-lg px-3 py-2 text-left transition-colors ${
                    preset.id === selectedId
                      ? "bg-primary-500/10 ring-1 ring-primary-500"
                      : "hover:bg-black/[0.04] dark:hover:bg-white/5"
                  }`}
                >
                  <span className="flex items-center gap-1.5">
                    <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                      {preset.name}
                    </span>
                    {preset.id === selectedId && dirty && (
                      <span
                        aria-label="Unsaved changes"
                        className="h-1.5 w-1.5 shrink-0 rounded-full bg-warning-500"
                      />
                    )}
                  </span>
                  <span className="mt-0.5 block truncate text-[11px] text-gray-500 dark:text-gray-400">
                    {preset.sections.length} section{preset.sections.length === 1 ? "" : "s"}
                    {preset.target.seriesSlug ? ` · ${preset.target.seriesSlug}` : ""}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => void remove(preset)}
                  aria-label={`Delete ${preset.name}`}
                  className="rounded-md p-1.5 text-gray-400 opacity-0 transition-opacity hover:text-error-500 focus:opacity-100 group-hover:opacity-100"
                >
                  <HiOutlineTrash className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}

        {newName === null ? (
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<HiPlus className="h-4 w-4" />}
            onClick={() => setNewName("")}
          >
            New preset
          </Button>
        ) : (
          <div className="space-y-2 rounded-xl border border-gray-200 p-2 dark:border-gray-800">
            <input
              aria-label="New preset name"
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
              onKeyDown={(event) => event.key === "Enter" && void create()}
              placeholder="Superkids 3 — standard lesson"
              className="w-full rounded-lg border border-gray-200 bg-background px-2.5 py-1.5 text-sm placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-gray-700"
            />
            <div className="flex justify-end gap-1.5">
              <Button variant="ghost" size="sm" onClick={() => setNewName(null)}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={create}
                disabled={newName.trim() === ""}
                isLoading={saving}
              >
                Create
              </Button>
            </div>
          </div>
        )}
      </aside>

      <section className="min-w-0 flex-1 overflow-y-auto">
        {!draft ? (
          <div className="flex h-full items-center justify-center rounded-xl border border-dashed border-gray-300 dark:border-gray-700">
            <p className="body-sm px-6 text-center text-gray-500 dark:text-gray-400">
              Pick a preset to edit its book scope and sections.
            </p>
          </div>
        ) : (
          <div className="space-y-6 pr-1">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div className="min-w-0 flex-1 space-y-1.5">
                <label htmlFor="preset-name" className="label block">
                  Name
                </label>
                <input
                  id="preset-name"
                  value={draft.name}
                  onChange={(event) => edit({ name: event.target.value })}
                  className="w-full max-w-md rounded-lg border border-gray-200 bg-background px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-gray-700"
                />
              </div>
              {dirty && (
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    onClick={() => {
                      if (selected) setDraft(toDraft(selected));
                      setDirty(false);
                      setEditedLinks([]);
                    }}
                  >
                    Discard
                  </Button>
                  <Button onClick={save} isLoading={saving}>
                    Save preset
                  </Button>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <span className="label block">Book this preset is written for</span>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Where the generator opens when the preset is applied. The lesson itself is always
                picked per run.
              </p>
              <div className="grid max-w-xl grid-cols-3 gap-3">
                <BookScopePicker
                  idPrefix="preset-scope"
                  value={{
                    seriesSlug: draft.seriesSlug,
                    levelId: draft.levelId,
                    componentId: draft.componentId,
                  }}
                  onChange={(scope) => edit(scope)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <span className="label block">Sections</span>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                One per kind of slide the preset adds, in the order the slides are written.
              </p>

              {draft.sections.length === 0 ? (
                <p className="rounded-xl border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
                  No sections yet. Add one for every kind of slide this preset should write.
                </p>
              ) : (
                <Sortable
                  items={draft.sections}
                  onReorder={(next) => edit({ sections: next })}
                  handle
                  strategy={verticalListSortingStrategy}
                >
                  {(section, index, dragHandle) => (
                    <SectionRow
                      section={section}
                      index={index}
                      followedName={
                        section.sectionPresetId
                          ? (savedSections.find((one) => one.id === section.sectionPresetId)
                              ?.name ?? null)
                          : null
                      }
                      dragHandle={dragHandle}
                      onEdit={() => openEditor(section)}
                      onUnlink={() => unlinkSection(section)}
                      onRemove={() =>
                        edit({ sections: draft.sections.filter((one) => one.id !== section.id) })
                      }
                    />
                  )}
                </Sortable>
              )}

              <Button
                variant="ghost"
                size="sm"
                leftIcon={<HiPlus className="h-4 w-4" />}
                onClick={() => openEditor(null)}
              >
                Add section
              </Button>
            </div>
          </div>
        )}
      </section>

      {isEditorOpen && draft && (
        <SectionEditorModal
          isOpen={isEditorOpen}
          onClose={() => setIsEditorOpen(false)}
          onSave={upsertSection}
          allowSubslides
          targetBook={null}
          bookScope={{
            seriesSlug: draft.seriesSlug,
            levelId: draft.levelId,
            componentId: draft.componentId,
          }}
          {...(editing ? { section: editing } : {})}
        />
      )}
    </div>
  );
}
